import React, { useCallback } from 'react';

import { sort } from 'ramda';
import { batch, useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';

import PoliciesModal from 'components/shared/TermsAndConds/PoliciesModal';
import { ACCOUNT_TYPE_ADMIN, ACCOUNT_TYPE_AGENT, ACCOUNT_TYPE_OWNER } from 'constants/index';
import { DASHBOARD_PATH } from 'constants/routes';
import { APP_HAS_CHANGES_TEXT } from 'constants/texts';
import { useAccountPath } from 'hooks/useAccountPath';
import { logoutAsThunk, logoutThunk } from 'store/auth/thunks';
import { setTermsOfServiceModalCloseAction } from 'store/terms_of_use';
import { termsOfServiceModalSelector } from 'store/terms_of_use/selectors';
import { appHasChangesSelector } from 'store/unsavedChanges/selectors';
import { changeAccountAction } from 'store/user';
import {
  hasAdminAccountSelector,
  hasAgentAccountSelector,
  hasOwnerAccountSelector,
  isAgentSelector,
  isOwnerSelector,
  isSuperAdminSelector,
  userSelector,
} from 'store/user/selectors';

export function useMenu() {
  const dispatch = useDispatch();
  const getPath = useAccountPath();
  const history = useHistory();
  const user = useSelector(userSelector);
  const hasAdminAccount = useSelector(hasAdminAccountSelector);
  const hasOwnerAccount = useSelector(hasOwnerAccountSelector);
  const hasAgentAccount = useSelector(hasAgentAccountSelector);
  const isSuperAdmin = useSelector(isSuperAdminSelector);
  const isOwner = useSelector(isOwnerSelector);
  const isAgent = useSelector(isAgentSelector);
  const doesAppHaveChanges = useSelector(appHasChangesSelector);

  const handleLogout = useCallback(() => {
    if (doesAppHaveChanges && !window.confirm(APP_HAS_CHANGES_TEXT)) {
      return;
    }

    batch(() => {
      dispatch(logoutAsThunk());
      dispatch(logoutThunk());
    });
  }, [dispatch, doesAppHaveChanges]);

  const changeAccount = useCallback(
    (accId) => {
      dispatch(changeAccountAction(accId));

      history.push(getPath(DASHBOARD_PATH));
    },
    [dispatch, getPath, history],
  );

  return {
    user,
    hasAdminAccount,
    hasOwnerAccount,
    hasAgentAccount,
    isSuperAdmin,
    isOwner,
    isAgent,
    handleLogout,
    changeAccount,
  };
}

export const prepareAccountsLinks = (list) => {
  const comp = (a, b) => (a.companyName < b.companyName ? -1 : 1);
  const supers = sort(
    comp,
    list.filter((acc) => acc.type === ACCOUNT_TYPE_ADMIN),
  );
  const agents = sort(
    comp,
    list.filter((acc) => acc.type === ACCOUNT_TYPE_AGENT),
  );
  const suppliers = sort(
    comp,
    list.filter((acc) => acc.type === ACCOUNT_TYPE_OWNER),
  );

  return [...supers, ...agents, ...suppliers];
};

export const userMenuLabelMap = {
  [ACCOUNT_TYPE_ADMIN]: 'S',
  [ACCOUNT_TYPE_AGENT]: 'A',
  [ACCOUNT_TYPE_OWNER]: 'S',
};

export const useTermsModal = () => {
  const dispatch = useDispatch();
  const termsOfServiceModalStoreState = useSelector(termsOfServiceModalSelector);
  const togglePoliciesModal = useCallback(() => {
    dispatch(setTermsOfServiceModalCloseAction());
  }, [dispatch]);

  return {
    modal: (
      <PoliciesModal
        isOpen={termsOfServiceModalStoreState.isOpen}
        toClose={togglePoliciesModal}
        type={termsOfServiceModalStoreState.type}
      />
    ),
  };
};
